import { useCallback } from 'react'
import api from '@/services/api'
import { toast } from 'sonner'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { CloserLead, CloserLeadFormData } from '@/types'
import {
  validateEcommercePlatformField,
  validateLeadPartnerChoice,
  validateRequiredCloserFields,
} from '@/utils/leadRegistrationValidation'
import { normalizeEcommercePlatform } from '@/constants/ecommercePlatforms'
import { isRocketInstance } from '@/config/instanceProfile'
import { requestLeadConvertOptions, type ConvertLeadOptions } from '@/stores/convertLeadDialogStore'

interface UseCloserLeadManagerMutationsParams {
  selectedLead: CloserLead | null
  userId: string
  onSuccess: () => void
  handleCloseModal: (options?: { skipDraftSave?: boolean }) => void
  setSaving: (v: boolean) => void
  setBackendErrors: (errors: Record<string, string>) => void
  clearDraftForUser: () => void
  setHighlightEmpresa?: (value: boolean) => void
  setHighlightContato?: (value: boolean) => void
}

export function useCloserLeadManagerMutations({
  selectedLead,
  userId,
  onSuccess,
  handleCloseModal,
  setSaving,
  setBackendErrors,
  clearDraftForUser,
  setHighlightEmpresa,
  setHighlightContato,
}: UseCloserLeadManagerMutationsParams) {
  const handleAddLead = useCallback(async (fd: CloserLeadFormData) => {
    setSaving(true)
    setBackendErrors({})

    const requiredErrors = {
      ...validateRequiredCloserFields(fd),
      ...(isRocketInstance ? validateLeadPartnerChoice(fd, { requireAnswer: true }) : {}),
    }
    if (Object.keys(requiredErrors).length > 0) {
      setBackendErrors(requiredErrors)
      if (requiredErrors.empresa) {
        setHighlightEmpresa?.(true)
      }
      if (requiredErrors.contato) {
        setHighlightContato?.(true)
      }
      const missingEmpresa = Boolean(requiredErrors.empresa)
      const missingContato = Boolean(requiredErrors.contato)
      toast.error(
        missingEmpresa && missingContato
          ? 'Preencha o nome do contato e a empresa'
          : missingEmpresa
            ? 'Informe o nome da empresa para cadastrar o lead'
            : missingContato
              ? 'Informe o nome do contato'
              : requiredErrors.temParceiro || requiredErrors.parceiroId || 'Preencha os campos obrigatórios',
      )
      setSaving(false)
      return
    }

    const platformErrors = validateEcommercePlatformField(fd.plataforma)
    if (Object.keys(platformErrors).length > 0) {
      setBackendErrors(platformErrors)
      toast.error(platformErrors.plataforma || 'Plataforma inválida')
      setSaving(false)
      return
    }

    const { temParceiro: _temParceiro, ...fields } = fd
    const payload = {
      ...fields,
      plataforma: fd.plataforma?.trim()
        ? normalizeEcommercePlatform(fd.plataforma) || fd.plataforma.trim()
        : '',
    }

    try {
      const response = await api.post('/closer-leads', payload)
      clearDraftForUser()
      const created = response.data?.data ?? response.data
      const movedToFirstMeeting = Boolean(created?.closerId) && created.closerId !== userId
      toast.success(
        movedToFirstMeeting
          ? `Lead cadastrado na carteira de ${created.closerName || 'quem marcou a primeira reunião'}`
          : 'Lead cadastrado com sucesso!',
      )
      handleCloseModal({ skipDraftSave: true })
      onSuccess()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: { message?: string; details?: { field?: string; message: string }[] }; message?: string; errors?: Record<string, string> } } }
      const status = axiosErr.response?.status
      const respData = axiosErr.response?.data
      const validationDetails = respData?.error?.details ?? respData?.errors

      if (status === 400 && Array.isArray(validationDetails)) {
        const mapped: Record<string, string> = {}
        for (const e of validationDetails) {
          if (e && typeof e === 'object' && 'field' in e && e.field) mapped[e.field as string] = e.message
        }
        setBackendErrors(mapped)
        toast.error('Corrija os campos destacados')
      } else if (status === 409) {
        const message = respData?.error?.message ?? respData?.message ?? 'Lead duplicado encontrado'
        if (message.toLowerCase().includes('telefone')) {
          setBackendErrors({ telefone: message })
        } else if (message.toLowerCase().includes('email') || message.toLowerCase().includes('e-mail')) {
          setBackendErrors({ email: message })
        } else if (message.toLowerCase().includes('empresa')) {
          setBackendErrors({ empresa: message })
        }
        toast.error(message)
      } else {
        toast.error(respData?.error?.message ?? respData?.message ?? 'Erro ao cadastrar lead')
      }
    } finally {
      setSaving(false)
    }
  }, [onSuccess, handleCloseModal, userId, setSaving, setBackendErrors, clearDraftForUser, setHighlightEmpresa])

  const handleUpdateLead = useCallback(async (id: string, fd: CloserLeadFormData) => {
    setSaving(true)
    setBackendErrors({})

    const partnerErrors = isRocketInstance ? validateLeadPartnerChoice(fd, { requireAnswer: false }) : {}
    if (Object.keys(partnerErrors).length > 0) {
      setBackendErrors(partnerErrors)
      toast.error(partnerErrors.parceiroId || 'Informe o parceiro')
      setSaving(false)
      return
    }

    const platformErrors = validateEcommercePlatformField(fd.plataforma)
    if (Object.keys(platformErrors).length > 0) {
      setBackendErrors(platformErrors)
      toast.error(platformErrors.plataforma || 'Plataforma inválida')
      setSaving(false)
      return
    }

    const { temParceiro: _temParceiro, ...fields } = fd
    const payload = {
      ...fields,
      plataforma: fd.plataforma?.trim()
        ? normalizeEcommercePlatform(fd.plataforma) || fd.plataforma.trim()
        : '',
    }

    try {
      await api.patch(`/closer-leads/${id}`, payload)
      toast.success('Lead atualizado com sucesso!')
      handleCloseModal()
      onSuccess()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: Record<string, string> } } }
      setBackendErrors(axiosErr.response?.data?.errors || {})
      toast.error(getFriendlyError(err, 'Erro ao atualizar lead'))
    } finally {
      setSaving(false)
    }
  }, [onSuccess, handleCloseModal, setSaving, setBackendErrors])

  const handleDeleteLead = useCallback(async (id: string) => {
    if (!window.confirm('Excluir este registro permanentemente?')) return
    try {
      await api.delete(`/closer-leads/${id}`)
      toast.success('Lead excluído!')
      handleCloseModal()
      onSuccess()
    } catch {
      toast.error('Erro ao excluir lead')
    }
  }, [onSuccess, handleCloseModal])

  const handleRegTentativa = useCallback(async (
    leadId: string,
    data: { canal: string; resultado: string; obs: string }
  ) => {
    try {
      await api.post(`/closer-leads/${leadId}/contato`, data)
      toast.success('Interação registrada!')
      onSuccess()
    } catch {
      toast.error('Erro ao registrar interação')
    }
  }, [onSuccess])

  const handleConvertToDeal = useCallback(async (id: string) => {
    if (selectedLead?.dealId) {
      toast.info('Este lead já foi convertido em uma Negociação')
      return
    }
    let convertOptions: ConvertLeadOptions | null = null
    if (isRocketInstance) {
      convertOptions = await requestLeadConvertOptions({
        leadId: id,
        empresa: selectedLead?.empresa,
        contato: selectedLead?.contato,
        parceiroId: selectedLead?.parceiroId,
      })
      if (!convertOptions) return
    } else if (!window.confirm('Deseja converter este lead em uma Negociação direta?')) {
      return
    }
    try {
      await api.post(`/closer-leads/${id}/converter`, { etapa: 'qualificado', valor: 0, ...convertOptions })
      toast.success('Lead convertido com sucesso!')
      handleCloseModal()
      onSuccess()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error('Erro ao converter lead: ' + (axiosErr.response?.data?.message || axiosErr.message))
    }
  }, [selectedLead, onSuccess, handleCloseModal])

  const handleAddPending = useCallback(async (leadId: string, text: string, date?: string) => {
    try {
      if (!selectedLead) return
      const pendencias = [...(selectedLead.pendencias || [])]
      pendencias.push({
        id: Date.now(),
        texto: text,
        dataRetorno: date || null,
        resolved: false,
        createdAt: new Date().toISOString(),
      } as (typeof pendencias)[number])
      await api.patch(`/closer-leads/${leadId}`, { pendencias })
      toast.success('Pendência adicionada!')
      onSuccess()
    } catch {
      toast.error('Erro ao adicionar pendência')
    }
  }, [selectedLead, onSuccess])

  const togglePending = useCallback(async (leadId: string, pendingId: number) => {
    try {
      if (!selectedLead?.pendencias) return
      const pendencias = selectedLead.pendencias.map(p =>
        p.id === pendingId ? { ...p, resolved: !p.resolved, resolvedAt: !p.resolved ? new Date().toISOString() : null } : p
      )
      await api.patch(`/closer-leads/${leadId}`, { pendencias })
      toast.success('Pendência atualizada!')
      onSuccess()
    } catch {
      toast.error('Erro ao atualizar pendência')
    }
  }, [selectedLead, onSuccess])

  const removePending = useCallback(async (leadId: string, pendingId: number) => {
    try {
      if (!selectedLead?.pendencias) return
      const pendencias = selectedLead.pendencias.filter(p => p.id !== pendingId)
      await api.patch(`/closer-leads/${leadId}`, { pendencias })
      toast.success('Pendência removida!')
      onSuccess()
    } catch {
      toast.error('Erro ao remover pendência')
    }
  }, [selectedLead, onSuccess])

  return {
    handleAddLead,
    handleUpdateLead,
    handleDeleteLead,
    handleRegTentativa,
    handleConvertToDeal,
    handleAddPending,
    togglePending,
    removePending,
  }
}
